<script setup lang="ts">import { computed as _computed } from 'vue';

import { RsCheckbox } from "@/components";

import { $can } from "@/services/UtilitiesService";

import { useTagStore } from "@/stores/tags-store";
import { useFiltersStore } from "@/stores/filters-store";

import type { TagType } from "@/models/TagType";
import type { Tag, TagFilter } from "@/models/Tag";

defineEmits<(e: "enterManagementMode", tagType: TagType) => void>();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const canAnnotate = _computed(() => {
  return $can("annotate");
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const allTags = _computed(() => {
  return useTagStore().tags.map((t) => t.name);
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectedTags = _computed(() => {
  return useFiltersStore().filter.tags;
});

const tagTypes = _computed(() => {
  return useTagStore().tagTypes.filter((f) => f.tagRestriction !== "instanceAutoTag");
});

function availableTags(tagType: string): Tag[] {
  const tagFilter: TagFilter = useFiltersStore().filter.tags.find((f) => f.tagType === tagType) || {
    tagType,
    includeBlanks: false,
    tags: [],
  };
  if (tagFilter.tags.includes("Any")) {
    return [];
  }

  const tags: Tag[] = useTagStore()
    .tags.filter(
      (a) =>
        !tagFilter.tags.includes(a.name) &&
        !!(useTagStore().fileTags || []).find((f) => f.name === a.name && f.tagType === a.tagType) &&
        a.tagType === tagType,
    )
    .sort((l, r) => {
      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return collator.compare(l.name, r.name);
    });
  return tags;
}

function getTagFiltersByType(tagType: string): string[] {
  const tagFilter: TagFilter = useFiltersStore().filter.tags.find((f) => f.tagType === tagType) || {
    tagType,
    includeBlanks: false,
    tags: [],
  };
  return tagFilter.tags;
}

function canAddNew(tagType: string): boolean {
  return availableTags(tagType).length > 0;
}

function shouldIncludeBlanksDisabled(tagType: string): boolean {
  const tagFilter: TagFilter = useFiltersStore().filter.tags.find((f) => f.tagType === tagType) || {
    tagType,
    includeBlanks: false,
    tags: [],
  };
  return tagFilter.tags.length === 0;
}

function shouldIncludeBlanks(tagType: string): boolean {
  const tagFilter: TagFilter = useFiltersStore().filter.tags.find((f) => f.tagType === tagType) || {
    tagType,
    includeBlanks: false,
    tags: [],
  };
  return tagFilter.includeBlanks;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isFixedType(tagType: TagType): boolean {
  if (tagType === null) {
    return false;
  }

  return (
    /^fixed/i.test(tagType.tagRestriction) ||
    tagType.tagRestriction === "autoTag" ||
    tagType.tagRestriction === "instanceAutoTag"
  );
}

function onTagSelect(tagType: string, event: Event) {
  const selectElement = event.srcElement as HTMLSelectElement;
  if (!selectElement?.value) {
    return;
  }

  useFiltersStore().addTag({ tagType, tag: selectElement.value });
  selectElement.value = "";
}

function onTagRemove(tagType: string, tag: string) {
  useFiltersStore().removeTag({ tagType, tag });
}

function onIncludeBlanksChange(tagType: string, includeBlanks: boolean) {
  useFiltersStore().includeBlanksForTagType({ tagType, includeBlanks });
}
</script>
<template>
  <div v-for="(tagType, index) in tagTypes" :key="`${tagType.name}=${index}`">
    <div
      v-if="getTagFiltersByType(tagType.name).length || canAddNew(tagType.name)"
      class="tw-mx-2 tw-mb-2 tw-grid tw-grid-cols-4 tw-gap-2"
    >
      <label class="wrapped" :for="`${tagType.name}-TagFilter`">{{ tagType.name }}</label>
      <div class="wrapped tw-col-span-2">
        <select
          v-if="canAddNew(tagType.name)"
          :id="`${tagType.name}-TagFilter`"
          class="tw-b-r tw-w-full tw-px-2 tw-py-1"
          @change.prevent="onTagSelect(tagType.name, $event)"
        >
          <option selected value="">Please select ...</option>
          <option value="Any">Any</option>
          <template v-for="tag in availableTags(tagType.name)" :key="`${tag.name}`">
            <option :value="tag.name">
              {{ tag.name }}
            </option>
          </template>
        </select>
        <button
          v-for="tag in getTagFiltersByType(tagType.name)"
          :key="tag"
          class="tw-m-1 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-border tw-px-1 tw-py-px hover:tw-opacity-50"
          :title="`Delete Tag: ${tag}`"
          @click.prevent="onTagRemove(tagType.name, tag)"
        >
          <strong class="tw-truncate">{{ tag }}</strong>
          <i class="fas fa-times-circle tw-mt-px"></i>
        </button>
      </div>
      <RsCheckbox
        :id="`${tagType.name}-incblanks`"
        :disabled="shouldIncludeBlanksDisabled(tagType.name)"
        :model-value="shouldIncludeBlanks(tagType.name)"
        @change="onIncludeBlanksChange(tagType.name, $event)"
        >Inc Blanks</RsCheckbox
      >
    </div>
  </div>
</template>
<style scoped>
.text-underline {
  text-decoration: underline;
}
.max-h-64 {
  max-height: 16rem;
}
</style>
